(() => {
  'use strict';

  const componentName = 'floating-product-videos';
  const youtubeHosts = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);
  const youtubeIdPattern = /^[A-Za-z0-9_-]{11}$/;

  class FloatingProductVideos extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.card = this.querySelector('[data-floating-video-card]');
      this.preview = this.querySelector('[data-floating-video-preview]');
      this.thumbnail = this.querySelector('[data-floating-video-thumbnail]');
      this.player = this.querySelector('[data-floating-video-player]');
      this.navigation = this.querySelector('[data-floating-video-navigation]');
      this.previousButton = this.querySelector('[data-floating-video-previous]');
      this.nextButton = this.querySelector('[data-floating-video-next]');
      this.count = this.querySelector('[data-floating-video-count]');
      this.minimizeButton = this.querySelector('[data-floating-video-minimize]');
      this.launcher = this.querySelector('[data-floating-video-launcher]');
      this.launcherThumbnail = this.querySelector('[data-floating-video-launcher-thumbnail]');
      this.videoIds = this.readVideoIds();

      if (!this.videoIds.length || !this.hasRequiredElements()) {
        this.remove();
        return;
      }

      this.currentIndex = 0;
      this.isPlaying = false;
      this.bindEvents();
      this.navigation.hidden = this.videoIds.length < 2;
      this.updatePreview();
      this.hidden = false;
    }

    hasRequiredElements() {
      return Boolean(
        this.card &&
          this.preview &&
          this.thumbnail &&
          this.player &&
          this.navigation &&
          this.previousButton &&
          this.nextButton &&
          this.count &&
          this.minimizeButton &&
          this.launcher &&
          this.launcherThumbnail
      );
    }

    readVideoIds() {
      const dataElement = this.querySelector('[data-floating-video-urls]');
      if (!dataElement) return [];

      let values;
      try {
        values = JSON.parse(dataElement.textContent);
      } catch (error) {
        return [];
      }

      if (!Array.isArray(values)) return [];

      const uniqueIds = new Set();
      values.forEach((value) => {
        if (typeof value !== 'string') return;
        const videoId = this.extractYoutubeId(value);
        if (videoId) uniqueIds.add(videoId);
      });

      return Array.from(uniqueIds);
    }

    extractYoutubeId(value) {
      let url;
      try {
        url = new URL(value);
      } catch (error) {
        return null;
      }

      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

      const hostname = url.hostname.toLowerCase();
      let videoId = null;

      if (hostname === 'youtu.be') {
        videoId = url.pathname.split('/').filter(Boolean)[0] || null;
      } else if (youtubeHosts.has(hostname)) {
        if (url.pathname === '/watch') {
          videoId = url.searchParams.get('v');
        } else {
          const pathParts = url.pathname.split('/').filter(Boolean);
          if (['shorts', 'embed', 'live'].includes(pathParts[0])) {
            videoId = pathParts[1] || null;
          }
        }
      }

      return videoId && youtubeIdPattern.test(videoId) ? videoId : null;
    }

    bindEvents() {
      this.preview.addEventListener('click', () => this.playCurrentVideo());
      this.previousButton.addEventListener('click', () => this.changeVideo(-1));
      this.nextButton.addEventListener('click', () => this.changeVideo(1));
      this.minimizeButton.addEventListener('click', () => this.minimize());
      this.launcher.addEventListener('click', () => this.reopen());
    }

    updatePreview() {
      const videoId = this.videoIds[this.currentIndex];
      const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      this.thumbnail.src = thumbnailUrl;
      this.thumbnail.alt = `Product video ${this.currentIndex + 1} of ${this.videoIds.length}`;
      this.preview.setAttribute(
        'aria-label',
        `Play product video ${this.currentIndex + 1} of ${this.videoIds.length}`
      );
      this.count.textContent = `${this.currentIndex + 1} / ${this.videoIds.length}`;
    }

    playCurrentVideo() {
      this.stopPlayback();

      const videoId = this.videoIds[this.currentIndex];
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0`;
      iframe.title = `Product video ${this.currentIndex + 1} of ${this.videoIds.length}`;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

      this.player.replaceChildren(iframe);
      this.preview.hidden = true;
      this.player.hidden = false;
      this.isPlaying = true;
    }

    stopPlayback() {
      this.player.replaceChildren();
      this.player.hidden = true;
      this.preview.hidden = false;
      this.isPlaying = false;
    }

    changeVideo(direction) {
      const shouldResumePlayback = this.isPlaying;
      this.stopPlayback();
      this.currentIndex =
        (this.currentIndex + direction + this.videoIds.length) % this.videoIds.length;
      this.updatePreview();

      if (shouldResumePlayback) this.playCurrentVideo();
    }

    minimize() {
      this.stopPlayback();
      this.launcherThumbnail.src = this.thumbnail.src;
      this.thumbnail.removeAttribute('src');
      this.card.hidden = true;
      this.launcher.hidden = false;
      this.launcher.focus();
    }

    reopen() {
      this.launcher.hidden = true;
      this.updatePreview();
      this.launcherThumbnail.removeAttribute('src');
      this.card.hidden = false;
      this.preview.focus();
    }
  }

  if (!customElements.get(componentName)) {
    customElements.define(componentName, FloatingProductVideos);
  }
})();
